import { formatAnalysisValue } from "../formatters"
import { INitrogenAnalysis, INitrogenAnalysisAlloc } from "../nitrogen/types"
import { FieldViewModel, groupBy, IGenericAnalysis, IGenericAnalysisAlloc, IPcnAnalysis, isArrayEmpty, isNumber, ISoilAnalysis, selectMany } from "../omnia"
import { IAnalysisValueColumn, IDataLayerZone, ISoilService, NitrogenAnalysisType } from "../types"

export class RowViewModel {

    static createModelForZone(svc: ISoilService, fieldVm: FieldViewModel, zone: IDataLayerZone): RowViewModel {

        var a: IAllocTarget = {
            Field: fieldVm,
            Description: fieldVm.getDescription(),

            AllocToField: true,
            NumberOfZones: 1,

            Allocations: [],

            ZonesAllocations: [
                {
                    FieldId: fieldVm.fieldId,
                    ZoneId: zone.DataLayerZoneId
                }
            ]

        };

        var row = new RowViewModel(null, svc, [a]);
        row.zoneNumber = zone.Number;

        return row;
    }

    static createModelForField(svc: ISoilService, fieldVm: FieldViewModel): RowViewModel {

        var a: IAllocTarget = {

            Field: fieldVm,
            Description: fieldVm.getDescription(),

            AllocToField: true,
            NumberOfZones: null,

            Allocations: [{
                FieldId: fieldVm.fieldId,
                ZoneId: 0
            }],
            ZonesAllocations: []

        };

        return new RowViewModel(null, svc, [a]);

    }


    public soilAnalysis: ISoilAnalysis;
    public nitrogenAnalysis: INitrogenAnalysis;
    public pcnAnalysis: IPcnAnalysis;

    public alternateRow: boolean;

    public pastAnalysis: boolean;

    public hidden: boolean;

    public mouseOver: boolean;

    private _showHistory: boolean;
    public fieldHistory: RowViewModel[];

    public showAllocations: boolean;
    public orderedAllocations: IAllocTarget[];
    public allocationsCount: number

    public zoneNumber: number;

    public soilAnalysisLayerId: number;
    public pcnAnalysisLayerId: number;

    private _calculatedValues: any;


    constructor(
        public analysis: IGenericAnalysis,
        private svc: ISoilService,
        public allocations: IAllocTarget[] = []) {

        this.analysis = analysis || <any>{
            Laboratory: { Name: null }
        }

        if (analysis) {

            if (analysis["PcnAnalysisId"]) {
                this.pcnAnalysis = <IPcnAnalysis>analysis;
            }
            else if (analysis["NitrogenAnalysisId"]) {
                this.nitrogenAnalysis = <INitrogenAnalysis>analysis;
            }
            else {
                this.soilAnalysis = <ISoilAnalysis>analysis;
            }

        }

        this.setupAllocations();

        if (this.nitrogenAnalysis) {
            this.setupNitrogenAnalysis();
        }

    }

    protected setupNitrogenAnalysis() {

        let a = this.nitrogenAnalysis;

        if (this.allocations && this.allocations.length > 0) {

            let alloc = <INitrogenAnalysisAlloc>(this.allocations[0].Allocations[0]);

            let sns = (a.Nitrogen || 0) + (alloc.CropN || 0);

            if (a.AnalysisType == NitrogenAnalysisType.SMN) {
                sns += (alloc.SMN || 0);
            }

            this._calculatedValues = {
                SNS: sns
            };

        }

    }

    get fieldDescription(): string {

        if (this.allocations.length == 1) {
            var a = this.allocations[0];
            return a.Field.getDescription();
        }

    }

    get fieldDescriptionHtml(): string {

        if (this.allocations.length == 1) {
            var a = this.allocations[0];
            return a.Field.getDescription()
        }

    }

    get fieldId(): number {

        if (this.allocations.length == 1) {
            return this.allocations[0].Field.fieldId;
        }

    }

    get croppingId(): number {

        if (this.allocations.length == 1) {
            return this.allocations[0].Field.croppingId;
        }

    }

    get targetId(): number {

        if (this.allocations.length == 1) {
            var i = this.allocations[0].Field;
            return (i.isCropping() ? i.croppingId : i.fieldId);
        }

    }

    get field(): FieldViewModel {

        if (this.allocations.length == 1) {
            return this.allocations[0].Field;
        }

    }

    get unallocated(): boolean {
        if (!this.soilAnalysis) {
            return false;
        }

        var allocations = this.soilAnalysis.Allocations.filter(a => (a.ZoneAlloc && !!a.ZoneId) || (!a.ZoneAlloc && !a.ZoneId));

        return !allocations.length;
    }

    hasSoilAnalysis(): boolean {
        var a = this.soilAnalysis;
        return (a && !!a.SoilAnalysisId);
    }

    hasPcnAnalysis(): boolean {
        var a = this.pcnAnalysis;
        return (a && !!a.PcnAnalysisId);
    }

    hasNitrogenAnalysis(): boolean {
        var a = this.nitrogenAnalysis;
        return (a && !!a.NitrogenAnalysisId);
    }

    hasAnalysis(): boolean {
        return this.hasSoilAnalysis() || this.hasNitrogenAnalysis() || this.hasPcnAnalysis();
    }

    hasAnalysisValue(column: IAnalysisValueColumn): boolean {
        return this.analysis && isNumber(this.analysis[column.name]);
    }

    get SoilAnalysisId(): number {
        if (this.soilAnalysis) {
            return this.soilAnalysis.SoilAnalysisId;
        }
    }

    get NitrogenAnalysisId(): number {
        if (this.nitrogenAnalysis) {
            return this.nitrogenAnalysis.NitrogenAnalysisId;
        }
    }

    get PcnAnalysisId(): number {
        if (this.pcnAnalysis) {
            return this.pcnAnalysis.PcnAnalysisId;
        }
    }

    get Id(): number {
        return (this.NitrogenAnalysisId || this.SoilAnalysisId || this.PcnAnalysisId) || 0;
    }

    get AnalysisNumber() {
        return this.analysis && this.analysis.AnalysisNumber
    }

    get Laboratory(): { Name: string } {
        return this.analysis && this.analysis.Laboratory
    }

    get FieldReference() {
        return this.analysis && this.analysis.FieldReference
    }

    get PlotReference() {
        return this.analysis && this.analysis.PlotReference
    }

    get SamplingDate() {
        return this.analysis && this.analysis.SamplingDate
    }

    get CreatedOn() {
        return this.analysis && this.analysis.CreatedOn
    }

    getValue(name: string): any {
        if (this.analysis) {
            return this.analysis[name];
        }
    }

    getAnalysisValue(column: IAnalysisValueColumn): any {

        if (this._calculatedValues) {
            let value = this._calculatedValues[column.name];
            if (isNumber(value)) return value;
        }

        if (!column.alloc) {
            return formatAnalysisValue(this.analysis, column)
        }

        if (this.allocations && this.allocations.length > 0) {
            return this.allocations[0].Allocations[0][column.name];
        }

    }

    showGauge(column: IAnalysisValueColumn): boolean {
        return column && column.gauge;
    }

    getSingleAllocDate(): Date {

        if (this.analysis && (this.allocations.length == 1) && (this.allocations[0].Allocations.length == 1)) {
            return this.analysis.SamplingDate;
        }

    }

    setupAllocations() {

        var groups = (this.nitrogenAnalysis
            ? this.getNitrogenAnalysisAllocations(this.nitrogenAnalysis)
            : this.pcnAnalysis
                ? this.getPcnAnalysisAllocations(this.pcnAnalysis)
                : this.getSoilAnalysisAllocations(this.soilAnalysis)
        );

        this.allocations = groups.map(g => {

            var allocations = g.allocations.filter(a => !a.ZoneId);

            var vm = {
                Field: g.field,
                Description: "",

                AllocToField: !!allocations.length,
                NumberOfZones: groupBy(g.allocations.filter(a => !!a.ZoneId), a => a.ZoneId).length,

                Allocations: allocations,
                ZonesAllocations: g.allocations.filter(a => !!a.ZoneId)

            };

            var descr = vm.AllocToField ? g.field.getDescription(false) : g.field.field.Name;

            if (vm.NumberOfZones > 0) {
                descr += " (" + vm.NumberOfZones.toString() + " zone" + (vm.NumberOfZones > 1 ? "s" : "") + ")";
            }

            vm.Description = descr;

            return vm;

        });

        this.orderedAllocations =
            this.allocations.filter(a => a.AllocToField)
                .concat(this.allocations.filter(a => !a.AllocToField));

        this.allocationsCount = this.orderedAllocations.length

    }


    private getSoilAnalysisAllocations(sa: ISoilAnalysis): { field: FieldViewModel, allocations: IGenericAnalysisAlloc[] }[] {

        return groupBy((sa.Allocations || []), a => a.FieldId)
            .map(g => {
                return {
                    field: this.svc.findField(g.key),
                    allocations: g.values
                }
            }).filter(a => !!a.field);

    }

    private getPcnAnalysisAllocations(pcn: IPcnAnalysis): { field: FieldViewModel, allocations: IGenericAnalysisAlloc[] }[] {

        return groupBy((pcn.Allocations || []), a => a.FieldId)
            .map(g => {
                return {
                    field: this.svc.findField(g.key),
                    allocations: g.values
                }
            }).filter(a => !!a.field);

    }


    private getNitrogenAnalysisAllocations(na: INitrogenAnalysis): { field: FieldViewModel, allocations: IGenericAnalysisAlloc[] }[] {

        return groupBy((na.Allocations || []), a => a.CroppingId)
            .map(g => {
                return {
                    field: this.svc.findCropping(g.key),
                    allocations: g.values
                }
            }).filter(a => !!a.field);

    }

    hasAllocations(): boolean {
        return this.allocations && (this.allocations.length > 0);
    }

    groupByField(): RowViewModel[] {

        var allocSource: IAllocTarget[] = this.allocations.filter(a => a.AllocToField);

        var soilAlloc: IGenericAnalysisAlloc[] = this.soilAnalysis && this.soilAnalysis.Allocations
            ? this.soilAnalysis.Allocations.filter(a => !a.ZoneId && !a.ZoneAlloc)
            : this
                .pcnAnalysis &&
                this.pcnAnalysis.Allocations
                ? this.pcnAnalysis.Allocations.filter(a => !a.ZoneId) : null;

        if (soilAlloc) {

            var groups = groupBy(soilAlloc, a => a.FieldId)
                .map(g => {
                    return {
                        field: this.svc.findField(g.key),
                        allocations: g.values
                    }
                }).filter(a => !!a.field);

            allocSource = groups.map(g => {

                var vm = {
                    Field: g.field,
                    Description: "",

                    AllocToField: !!g.allocations.length,
                    NumberOfZones: 0,

                    Allocations: g.allocations,
                    ZonesAllocations: []

                };

                var descr = vm.AllocToField ? g.field.getDescription(false) : g.field.fieldName;

                vm.Description = descr;

                return vm;

            });

        }

        var allocations = selectMany(allocSource.map(a => {

            if (a.Allocations.length <= 1) {
                return [a];
            }

            return a.Allocations.map(fa => {

                return {
                    Field: a.Field,
                    Description: a.Description,
                    AllocToField: true,
                    NumberOfZones: a.NumberOfZones,
                    Allocations: [fa],
                    ZonesAllocations: []
                }

            });

        }));

        if (allocations.length > 0) {
            return allocations.map(a => new RowViewModel(this.analysis, this.svc, [a]));
        }

        return null;

    }

    get showHistory() {
        return this._showHistory;
    }

    set showHistory(value: boolean) {

        this._showHistory = value;

        if (this.fieldHistory) {
            this.fieldHistory.forEach(r => r.hidden = !value);
        }

    }

}


export interface IAllocTarget {

    /*
    * This is either a field or a cropping, depending on the analysis type:
    * - Soil analysis are allocated to fields
    * - Nitrogen analysis are allocated to croppings
    */
    Field: FieldViewModel;

    Description: string;

    AllocToField: boolean;
    NumberOfZones: number;

    /*
    * Those are field or cropping level allocations, without any zone level allocation
    */
    Allocations: IGenericAnalysisAlloc[];
    /*
    * Those are field zone level allocations, without other level allocation
    */
    ZonesAllocations: IGenericAnalysisAlloc[];

}
